/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { clausesApi, chambersApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import HelpButton from "@/components/HelpButton";
import {
    Plus, MagnifyingGlass, TrashSimple, Books, Copy, ArrowSquareOut,
} from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Clauses() {
    const [list, setList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [chambers, setChambers] = useState([]);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("all");
    const [scope, setScope] = useState("all");
    const [open, setOpen] = useState(false);

    const load = async () => {
        const params = {};
        if (q) params.q = q;
        if (cat !== "all") params.category = cat;
        if (scope !== "all") params.scope = scope;
        setList(await clausesApi.list(params).catch(() => []));
    };
    useEffect(() => { load(); }, [q, cat, scope]);
    useEffect(() => {
        clausesApi.categories().then(setCategories).catch(() => {});
        chambersApi.list().then(setChambers).catch(() => {});
    }, []);

    const grouped = useMemo(() => {
        const m = new Map();
        list.forEach((c) => {
            const k = c.category || "uncategorised";
            if (!m.has(k)) m.set(k, []);
            m.get(k).push(c);
        });
        return Array.from(m.entries());
    }, [list]);

    return (
        <div className="p-4 md:p-8 max-w-[1300px] mx-auto" data-testid="clauses-page">
            <div className="rule-line pt-3 mb-6">
                <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Drafting</div>
                <div className="flex items-center gap-2">
                    <h1 className="font-serif text-4xl tracking-tight">Clause Bank</h1>
                    <HelpButton topic="clauses" testid="help-clauses" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">Reusable boilerplate, jurisdiction, indemnity, arbitration and tailored clauses. Personal + chamber-shared.</p>
            </div>

            <div className="flex flex-wrap gap-2 mb-4 items-center" data-testid="clauses-toolbar">
                <div className="relative flex-1 min-w-[240px]">
                    <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title / body / tag…" className="pl-8" data-testid="clauses-search" />
                </div>
                <Select value={cat} onValueChange={setCat}>
                    <SelectTrigger className="w-[200px]" data-testid="clauses-cat-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All categories</SelectItem>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={scope} onValueChange={setScope}>
                    <SelectTrigger className="w-[160px]" data-testid="clauses-scope-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Mine + chambers</SelectItem>
                        <SelectItem value="mine">Mine only</SelectItem>
                        <SelectItem value="chamber">Chamber only</SelectItem>
                    </SelectContent>
                </Select>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="clauses-new-btn"><Plus size={14} /> New clause</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                        <DialogHeader>
                            <DialogTitle>New clause</DialogTitle>
                            <DialogDescription>Save a re-usable clause to your bank. Tag it for fast retrieval.</DialogDescription>
                        </DialogHeader>
                        <ClauseForm categories={categories} chambers={chambers} onDone={() => { setOpen(false); load(); }} />
                    </DialogContent>
                </Dialog>
            </div>

            {!list.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-sm text-muted-foreground text-center" data-testid="clauses-empty">
                    <Books size={32} weight="duotone" className="mx-auto mb-3 text-muted-foreground" />
                    <div>{q || cat !== "all" ? "No clauses match these filters." : "Your bank is empty — add your first clause."}</div>
                </div>
            ) : (
                <div className="space-y-6">
                    {grouped.map(([k, items]) => (
                        <section key={k} data-testid={`clauses-group-${k}`}>
                            <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2 border-b border-border pb-1">{k.replace(/_/g, " ")} <span className="text-muted-foreground/60">· {items.length}</span></div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {items.map((c) => <ClauseCard key={c.id} clause={c} onChange={load} />)}
                            </div>
                        </section>
                    ))}
                </div>
            )}
        </div>
    );
}

function ClauseCard({ clause, onChange }) {
    const [open, setOpen] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(clause.body);
            await clausesApi.use(clause.id);
            toast.success("Copied & usage tracked");
            onChange?.();
        } catch { toast.error("Clipboard failed"); }
    };
    const remove = async () => {
        try { await clausesApi.remove(clause.id); toast.success("Deleted"); onChange?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="border border-border bg-white rounded-md p-3" data-testid={`clause-${clause.id}`}>
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium">{clause.title}</span>
                        <span className="chip chip-doc text-xs">{clause.language}</span>
                        {clause.chamber_id && <span className="chip chip-ai text-xs">chamber</span>}
                        <span className="text-xs text-muted-foreground">used {clause.usage_count}×</span>
                    </div>
                    {clause.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                            {clause.tags.map((t) => <span key={t} className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground border border-border rounded-sm px-1.5 py-0.5">{t}</span>)}
                        </div>
                    )}
                    <pre className="text-xs whitespace-pre-wrap font-mono mt-2 line-clamp-3 text-muted-foreground">{clause.body}</pre>
                </div>
                <div className="flex flex-col gap-1 items-end">
                    <button onClick={copy} className="text-muted-foreground hover:text-foreground" aria-label="Copy" data-testid={`clause-copy-${clause.id}`}><Copy size={14} /></button>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-foreground" data-testid={`clause-view-${clause.id}`}><ArrowSquareOut size={14} /></button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl">
                            <DialogHeader>
                                <DialogTitle>{clause.title}</DialogTitle>
                                <DialogDescription>{clause.category.replace(/_/g, " ")} · {clause.language} · used {clause.usage_count}×</DialogDescription>
                            </DialogHeader>
                            <pre className="text-sm whitespace-pre-wrap font-mono border border-border bg-muted/30 rounded-sm p-3 max-h-[60vh] overflow-y-auto">{clause.body}</pre>
                            {clause.notes && <div className="text-xs italic text-muted-foreground">Notes: {clause.notes}</div>}
                            <div className="flex justify-end gap-2 pt-2">
                                <Button variant="outline" onClick={copy}><Copy size={14} /> Copy</Button>
                            </div>
                        </DialogContent>
                    </Dialog>
                    <AlertDialog>
                        <AlertDialogTrigger asChild>
                            <button className="text-muted-foreground hover:text-destructive" data-testid={`clause-delete-${clause.id}`}><TrashSimple size={14} /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Delete this clause?</AlertDialogTitle>
                                <AlertDialogDescription>Removes "{clause.title}" from your bank. Past uses are untouched.</AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </div>
        </div>
    );
}

function ClauseForm({ categories, chambers, onDone }) {
    const [v, setV] = useState({ title: "", category: "boilerplate", body: "", language: "en", tags: "", notes: "", chamber_id: "personal" });
    const submit = async () => {
        if (!v.title || !v.body) { toast.error("Title and body required"); return; }
        try {
            await clausesApi.create({
                title: v.title, category: v.category, body: v.body, language: v.language,
                notes: v.notes,
                tags: v.tags.split(",").map((x) => x.trim()).filter(Boolean),
                chamber_id: v.chamber_id === "personal" ? null : v.chamber_id,
            });
            toast.success("Clause saved");
            onDone?.();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    return (
        <div className="space-y-2" data-testid="clause-form">
            <Input placeholder="Title" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="clause-title" />
            <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2">
                    <Select value={v.category} onValueChange={(c) => setV({ ...v, category: c })}>
                        <SelectTrigger data-testid="clause-cat"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-[300px]">{categories.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <Select value={v.language} onValueChange={(l) => setV({ ...v, language: l })}>
                    <SelectTrigger data-testid="clause-lang"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="en">English</SelectItem>
                        <SelectItem value="hi">हिन्दी</SelectItem>
                        <SelectItem value="bilingual">Bilingual</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <Textarea rows={6} placeholder="Clause body — full text, ready to paste." value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} className="font-mono text-xs" data-testid="clause-body" />
            <Input placeholder="Tags — comma separated" value={v.tags} onChange={(e) => setV({ ...v, tags: e.target.value })} data-testid="clause-tags" />
            <Select value={v.chamber_id} onValueChange={(c) => setV({ ...v, chamber_id: c })}>
                <SelectTrigger data-testid="clause-chamber"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="personal">Personal (only me)</SelectItem>
                    {chambers.map((c) => <SelectItem key={c.id} value={c.id}>Share with {c.name}</SelectItem>)}
                </SelectContent>
            </Select>
            <Textarea rows={2} placeholder="Notes (when to use, edge cases)" value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} />
            <div className="flex justify-end pt-1"><Button onClick={submit} data-testid="clause-submit">Save</Button></div>
        </div>
    );
}
